import React, { useState, useEffect } from 'react';
import {
  Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow,
  TableCell, TableBody, Typography, Link,
} from '@mui/material';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import { DetailRow, InvTextField, InvSelect } from '../../components/InvField';
import { getConsolidation, createPoFromConsolidation } from '../../api/consolidation';
import { getStores } from '../../api/stockLedger';
import { getPurchasableVendors } from '../../api/vendor';
import useItemNames from '../../hooks/useItemNames';
import { normalizeConsolidation } from '../../utils/consolidationEntity';
import { formatDate, formatQty, generateTxnNumber } from '../../utils';
import { PRIORITY_LEVELS } from '../../constants';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

function storeLabel(store) {
  if (!store) return null;
  const code = store.store_code ? ` (${store.store_code})` : '';
  return `${store.store_name || ''}${code}`.trim() || null;
}

export default function ConsolidationDetail() {
  const { id } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const { resolveItem } = useItemNames();
  const stockBase = url.replace(/\/consolidations\/[^/]+$/, '');
  const materialBase = stockBase.replace(/\/stock$/, '/material');

  const [consolidation, setConsolidation] = useState(null);
  const [stores, setStores] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingPo, setCreatingPo] = useState(false);

  const poCreated = consolidation?.status === 'PO_CREATED' || Boolean(consolidation?.purchase_order_id);

  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      vendor_id: '',
      po_number: generateTxnNumber('PO'),
      priority: 'NORMAL',
      expected_delivery_date: '',
      delivery_address: '',
      payment_terms: '',
      remarks: '',
    },
  });

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [raw, storesData] = await Promise.all([
        getConsolidation(id),
        getStores().catch(() => []),
      ]);
      const normalized = normalizeConsolidation(raw);
      setConsolidation(normalized);
      setStores(normalizeList(storesData));
      const alreadyPo = normalized.status === 'PO_CREATED' || Boolean(normalized.purchase_order_id);
      if (!alreadyPo) {
        const vendorList = await getPurchasableVendors().catch(() => []);
        setVendors(vendorList);
      }
    } catch (e) {
      setError(e.message || 'Failed to load consolidation');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [id]);

  function resolveStoreName(storeId) {
    if (!storeId) return '—';
    const store = stores.find(s => (s.store_id || s.id) === storeId);
    return storeLabel(store) || storeId;
  }

  async function onCreatePo(data) {
    if (!can(INV_ACTIONS.CONSOLIDATION_CREATE)) return;
    setCreatingPo(true);
    try {
      // tenant_id/hospital_id aren't carried on the consolidation record itself —
      // resolve them off the central store's own record, same fallback used elsewhere.
      const centralStore = stores.find(s => (s.store_id || s.id) === consolidation.central_store_id);
      const result = await createPoFromConsolidation(id, {
        tenant_id: centralStore?.tenant_id || undefined,
        hospital_id: centralStore?.hospital_id || undefined,
        vendor_id: data.vendor_id,
        po_number: data.po_number,
        priority: data.priority,
        expected_delivery_date: data.expected_delivery_date || undefined,
        delivery_address: data.delivery_address || undefined,
        payment_terms: data.payment_terms || undefined,
        remarks: data.remarks || undefined,
      });
      const poId = result?.purchase_order_id
        || result?.po_id
        || result?.id
        || result?.purchase_order?.purchase_order_id
        || result?.purchase_order?.id;
      toast.success('Purchase order created from consolidation');
      if (poId) {
        history.push(`${materialBase}/po/${poId}`);
      } else {
        load();
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to create purchase order');
    } finally {
      setCreatingPo(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (error) {
    return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  }

  if (!consolidation) return null;

  const lines = consolidation.lines || [];
  const totalToPurchase = lines.reduce((sum, l) => sum + (Number(l.to_purchase) || 0), 0);

  return (
    <Box>
      <PageHeader
        eyebrow="Inventory Management › Consolidation"
        title={consolidation.consolidation_number || `Consolidation ${id}`}
        actions={
          <>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.push(`${stockBase}/consolidations`)}>
              New Consolidation
            </InvButton>
            {consolidation.purchase_order_id && (
              <InvButton
                size="small"
                tone="mint"
                onClick={() => history.push(`${materialBase}/po/${consolidation.purchase_order_id}`)}
              >
                View PO
              </InvButton>
            )}
          </>
        }
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <SectionCard
            title="Summary"
            actions={<StatusChip status={consolidation.status} />}
          >
            <DetailRow label="Central Store">
              {resolveStoreName(consolidation.central_store_id)}
            </DetailRow>
            <DetailRow label="Period">
              {formatDate(consolidation.from_date)} — {formatDate(consolidation.to_date)}
            </DetailRow>
            <DetailRow label="Shortfall Items">{lines.length}</DetailRow>
            <DetailRow label="Total To Purchase">{formatQty(totalToPurchase)}</DetailRow>
            {consolidation.purchase_order_id && (
              <DetailRow label="Purchase Order">{consolidation.purchase_order_id}</DetailRow>
            )}
            <DetailRow label="Created">{formatDate(consolidation.created_at)}</DetailRow>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={8}>
          <SectionCard title="Shortfall Buy List">
            {lines.length === 0 ? (
              <Alert severity="success">No shortfall items — central stock covers all requisition demand.</Alert>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Total Required</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Central Available</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.warning.text, fontWeight: 600 }}>To Purchase</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Source Requisitions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((line, i) => {
                      const sourceIds = line.source_requisition_ids || [];
                      return (
                        <TableRow key={line.item_id || i}>
                          <TableCell>{resolveItem(line)}</TableCell>
                          <TableCell align="right">{formatQty(line.total_required)}</TableCell>
                          <TableCell align="right">{formatQty(line.available_central)}</TableCell>
                          <TableCell align="right" sx={{ color: IPD_COLORS.warning.text, fontWeight: 600 }}>
                            {formatQty(line.to_purchase)}
                          </TableCell>
                          <TableCell>
                            {sourceIds.length === 0 ? (
                              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>—</Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {sourceIds.map(reqId => (
                                  <Link
                                    key={reqId}
                                    component="button"
                                    type="button"
                                    underline="hover"
                                    onClick={() => history.push(`${stockBase}/requisitions/${reqId}`)}
                                    sx={{ fontSize: 12, cursor: 'pointer' }}
                                  >
                                    {String(reqId).slice(0, 8)}…
                                  </Link>
                                ))}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </SectionCard>
        </Grid>

        {lines.length > 0 && !poCreated && can(INV_ACTIONS.CONSOLIDATION_CREATE) && (
          <Grid item xs={12}>
            <SectionCard
              title="Create Purchase Order"
              subtitle="Raises a PO in procurement-service with source_type = CONSOLIDATION"
            >
              <Box component="form" onSubmit={handleSubmit(onCreatePo)}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="vendor_id"
                      control={control}
                      rules={{ required: 'Vendor is required' }}
                      render={({ field }) => (
                        <InvSelect
                          label="Vendor *"
                          value={field.value}
                          onChange={field.onChange}
                          options={vendors.map(v => ({
                            value: v.vendor_id || v.id,
                            label: v.vendor_code
                              ? `${v.vendor_name} (${v.vendor_code})`
                              : v.vendor_name,
                          }))}
                          error={errors.vendor_id}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="po_number"
                      control={control}
                      rules={{ required: 'PO number is required' }}
                      render={({ field }) => (
                        <InvTextField {...field} label="PO Number *" error={errors.po_number} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="priority"
                      control={control}
                      render={({ field }) => (
                        <InvSelect
                          label="Priority"
                          value={field.value}
                          onChange={field.onChange}
                          options={PRIORITY_LEVELS.map(p => ({ value: p, label: p }))}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="expected_delivery_date"
                      control={control}
                      render={({ field }) => (
                        <InvTextField
                          {...field}
                          label="Expected Delivery"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="payment_terms"
                      control={control}
                      render={({ field }) => <InvTextField {...field} label="Payment Terms" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="delivery_address"
                      control={control}
                      render={({ field }) => <InvTextField {...field} label="Delivery Address" />}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="remarks"
                      control={control}
                      render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <InvButton type="submit" loading={creatingPo}>
                        Create PO from Consolidation
                      </InvButton>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </SectionCard>
          </Grid>
        )}

        {poCreated && (
          <Grid item xs={12}>
            <Alert severity="info">
              A purchase order has already been created for this consolidation. PO conversion is one-time.
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
